import { Timestamp } from "firebase/firestore";

/**
 * Frontend mirror of `IssuedVersion` in
 * `functions/src/models/types.ts`. The Firestore document shape is the
 * same on both sides; we redeclare here because the frontend cannot
 * import from `functions/`.
 *
 * Stored at `{sourceDocPath}/versions/{versionId}`. Once written, an
 * IssuedVersion document is immutable. The corresponding binary lives
 * in Firebase Storage at `storagePath`.
 */
export interface IssuedVersion {
  id: string;
  versionString: string;        // "1.0", "1.1", "2.0"
  major: number;
  minor: number;
  revisionType: "initial" | "minor" | "major";
  issuedAt: Timestamp;
  issuedBy: {
    userId: string;
    userName: string;
    userEmail: string;
  };
  changeDescription: string;
  storagePath: string;
  filename: string;
  bytes: number;
}

/**
 * Parses a "major.minor" version string into its numeric components.
 * Returns null on malformed input.
 */
export function parseVersionString(
  v: string | null | undefined,
): { major: number; minor: number } | null {
  if (!v) return null;
  const match = v.match(/^(\d+)\.(\d+)$/);
  if (!match) return null;
  return { major: Number(match[1]), minor: Number(match[2]) };
}

/**
 * Computes the next minor version (e.g. "1.1" -> "1.2"). If the input
 * is null/undefined (never issued), returns "1.0" — the canonical first
 * issue.
 */
export function nextMinorVersion(current: string | null | undefined): string {
  const parsed = parseVersionString(current);
  if (!parsed) return "1.0";
  return `${parsed.major}.${parsed.minor + 1}`;
}

/**
 * Computes the next major version (e.g. "1.1" -> "2.0"). If the input
 * is null/undefined, returns "1.0".
 */
export function nextMajorVersion(current: string | null | undefined): string {
  const parsed = parseVersionString(current);
  if (!parsed) return "1.0";
  return `${parsed.major + 1}.0`;
}
